import { EllipsisTooltip } from 'App/Map/PanelDetails/OverviewCharts/EllipsisTooltip'
import { almondHullSplitColumn } from 'App/Map/PanelDetails/OverviewCharts/_utils/almondHullSplitColumn'
import {
  lastDays,
  lastHours,
  nextDays,
  periodTitle,
} from 'App/Map/PanelDetails/OverviewCharts/_utils/columnTitles'
import type { TValueGroupColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/types'
import { valueColumn } from 'App/Map/PanelDetails/OverviewCharts/_utils/valueColumn'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { unitConverter } from 'utils/unitConverter/unitConverter'

const SOUTHERN_HEMISPHERE_CHILL_COLUMNS = [
  ['chillHoursLessThan45_sinceMar1SumMinMedianMax_hours', 220, 'chillCumulativeHoursBelow45FMAR'],
  ['chillHoursBetween32And45_sinceMar1SumMinMedianMax_hours', 220, 'chillCumulativeHoursBetween32and45FMAR'],
  ['chillPortions_sinceMar1SumMinMedianMax_portions', 200, 'chillCumulativePortionsMAR'],
  ['chillHoursLessThan45_sinceMay1SumMinMedianMax_hours', 200, 'chillCumulativeHoursBelow45FMAY'],
  ['chillHoursBetween32And45_sinceMay1SumMinMedianMax_hours', 210, 'chillCumulativeHoursBetween32and45FMAY'],
  ['chillPortions_sinceMay1SumMinMedianMax_portions', 180, 'chillCumulativePortionsMAY'],
] as const

const NORTHERN_HEMISPHERE_CHILL_COLUMNS = [
  ['chillHoursLessThan45_sinceSep1SumMinMedianMax_hours', 255, 'chillCumulativeHoursBelow45FSEP'],
  ['chillHoursBetween32And45_sinceSep1SumMinMedianMax_hours', 255, 'chillCumulativeHoursBetween32and45FSEP'],
  ['chillPortions_sinceSep1SumMinMedianMax_portions', 210, 'chillCumulativePortionsSEP'],
  ['chillHoursLessThan45_sinceNov1SumMinMedianMax_hours', 255, 'chillCumulativeHoursBelow45FNOV'],
  ['chillHoursBetween32And45_sinceNov1SumMinMedianMax_hours', 255, 'chillCumulativeHoursBetween32and45FNOV'],
  ['chillPortions_sinceNov1SumMinMedianMax_portions', 210, 'chillCumulativePortionsNOV'],
] as const

const forecastDays = (count: number) => translate.phrases.banyanApp('Forecast {{count}} Days', { count })

const almondBloomColumn = (dataIndex: string, period: string, width: number): TColumn => ({
  title: (
    <EllipsisTooltip
      fullText={translate.phrases.templates('{{labelA}} {{labelB}} ({{labelC}})', {
        labelA: translate.phrases.banyanApp('Almond Bloom'),
        labelB: period,
        labelC: translate.phrases.banyanApp('Nonpareil'),
      })}
    />
  ),
  dataIndex,
  key: dataIndex,
  width,
  render: (value: number) =>
    !isNil(value) && value !== 0
      ? unitConverter.almondBloom(value).valueWithSuffix()
      : translate.phrases.templates('-'),
})

const beeHoursColumn = (dataIndex: string, period: string) =>
  valueColumn({
    dataIndex,
    title: periodTitle(translate.phrases.banyanApp('Bee Hours'), period),
    width: 225,
    converter: unitConverter.beeHours,
  })

export const cropDevelopmentColumns: Record<string, TValueGroupColumns> = {
  almond_bloom: {
    leadingColumn: 'blockName',
    getColumns: () => [
      almondBloomColumn('almondBloom_today_pct', translate.phrases.banyanApp('Today'), 325),
      almondBloomColumn('almondBloom_next7Days_pct', forecastDays(7), 350),
      almondBloomColumn('almondBloom_next14Days_pct', forecastDays(14), 350),
    ],
  },
  almond_hull_split: {
    leadingColumn: 'blockName',
    getColumns: () => [almondHullSplitColumn()],
  },
  bee_hours: {
    leadingColumn: 'blockName',
    getColumns: () => [
      beeHoursColumn('beeHours_last24hoursSum_hours', lastHours(24)),
      beeHoursColumn('beeHours_last7daysSum_hours', lastDays(7)),
      beeHoursColumn('beeHours_last30daysSum_hours', lastDays(30)),
      beeHoursColumn('beeHours_next7daysSum_hours', nextDays(7)),
      beeHoursColumn('beeHours_next14daysSum_hours', nextDays(14)),
    ],
  },
  chill: {
    leadingColumn: 'blockName',
    getColumns: ({ isSouthernHemisphere }) =>
      (isSouthernHemisphere ? SOUTHERN_HEMISPHERE_CHILL_COLUMNS : NORTHERN_HEMISPHERE_CHILL_COLUMNS).map(
        ([dataIndex, width, converter]) =>
          valueColumn({
            dataIndex,
            key: dataIndex.replace('SumMinMedianMax', 'Sum'),
            title: unitConverter[converter]().titleWithoutUnit(),
            width,
            converter: unitConverter[converter],
          }),
      ),
  },
}
